import React, { memo, useState, useEffect, useMemo, useCallback } from "react";
import PropTypes from "prop-types";
import classNames from "classnames";
import moment from "utils/moment/momentLocale";
import { useTranslation } from "react-i18next";

// styles
import "./style.scss";

// antd component
import { Form, Button, Input, Checkbox } from "antd";
const { TextArea } = Input;

// containers
import SelectFieldAxios from "containers/SelectFieldAxios";

// components
import CustomDate from "components/Forms/Fields/CustomDate";
import CustomTime from "components/Forms/Fields/CustomTime";
import CustomSelect from "components/Forms/Fields/CustomSelect";
// media components
import UploadMedia from "mobile/containers/UploadMedia";
import PreviewMedia from "components/Forms/Fields/PreviewMedia";

// utils
import api from "utils/api";
import { clean } from "utils/helpers/helper.general";
import { getStatusesList } from "utils/helpers/helper.generateData";
import { useRefState } from "utils/hooks";
import _local from "utils/localStorageUtils";

// global user
import { useChangeChildByID } from "engine/Contexts/User.context";

function SicknessForm({
  formData,
  // actions
  nextStep,
  prevStep,
  result,
  selectedStudent,
  setSelectedStudent,
  // default
  className,
}) {
  const childClassName = classNames("form-absence", className);
  const { t, i18n } = useTranslation();

  const localUser = _local.getLocalUser();
  const changeChildByID = useChangeChildByID();

  const now = !!formData?.date_from && moment(formData?.date_from) || moment();
  const nowTime = !!formData?.time_from && moment(formData?.time_from) || moment();
  const timeFormat = "HH:mm";
  const dateFormat = "DD.MM.YYYY";
  let initFormData = {
    status: "sick",
    remark: "",
    date_from: now,
    time_from: nowTime,
  };

  const [isDisabled, setIsDisabled] = useState(false);

  const [absenceStatus, setAbsenceStatus] = useState(
    formData?.status || initFormData?.status,
  );

  const [currentDay, setCurrentDay] = useState(now);
  const [currentTime, setCurrentTime] = useState(now);

  // only for teacher mode
  const [studentParent, studentParentRef, setStudentParent] = useRefState(
    false,
  );

  // for media
  const [mediaFiles, mediaFilesRef, setMediaFiles] = useRefState([]);
  const [uploading, setUploading] = useState(false);
  const [removeLoading, setRemoveLoading] = useState(false);

  const [AbsFormSickness] = Form.useForm();

  /*
   * Check current form data from parent
   * Set old data to the form
   */
  useEffect(() => {
    if (formData) {
      const hasValFormData = clean(formData);
      setIsDisabled(false);
      AbsFormSickness.setFieldsValue({
        type: hasValFormData?.type,
        remark: hasValFormData?.remark || initFormData?.remark,
        status: hasValFormData?.status || initFormData?.status,
        student: hasValFormData?.student?._id || hasValFormData?.student,
      });

      setAbsenceStatus(hasValFormData?.status || initFormData?.status);

      if (hasValFormData?.media) {
        setMediaFiles(hasValFormData?.media);
      }

      if (hasValFormData?.date_from) {
        setCurrentDay(moment(hasValFormData.date_from));
      } else {
        setCurrentDay(now);
      }

      if (hasValFormData?.time_from) {
        setCurrentTime(moment(hasValFormData.time_from));
      } else {
        setCurrentTime(nowTime);
      }

      if (hasValFormData?.status == "sickness-finish") {
        AbsFormSickness.setFieldsValue({
          date_to: hasValFormData?.date_to
            ? moment(hasValFormData.date_to)
            : null,
          time_to: hasValFormData?.time_to
            ? moment(hasValFormData.time_to)
            : null,
        });
      }

      if (hasValFormData?.student?._id) {
        setSelectedStudent(hasValFormData?.student);
        localUser?.role == "parent" &&
          changeChildByID(
            hasValFormData?.student?._id || hasValFormData?.student,
          );
        const contact_person =
          hasValFormData?.student?.contact_person?._id ||
          hasValFormData?.student?.contact_person;
        const parent =
          contact_person ||
          hasValFormData?.student?.parents?.[0]?._id ||
          hasValFormData?.student?.parents?.[0];
        setStudentParent(parent);
      }
    } else {
      setCurrentDay(now);
      setCurrentTime(nowTime);
    }
  }, [formData]);

  const onFinish = values => {
    let absenceBody = values;
    absenceBody.media = mediaFiles;
    result(absenceBody);
    AbsFormSickness.resetFields();
  };

  // Select student for current absence
  const selectStudents = useMemo(() => {
    // initial data for SelectFieldAxios component
    const newElementSelect = {
      label: t("labels.student"),
      name: "student",
      endponit: `students/list/select`,
      endponitSend: {},
      required: true,
      value: "",
      displayLabel: true,
      type: "axios-select",
      tagRender: props => (
        <span className="multiple-label-list">{props.label}</span>
      ),
      onSelect: (LabeledValue, option) => {
        setSelectedStudent(option?.item);
        localUser?.role == "parent" &&
          changeChildByID(option?.item?._id || option?.item);
        const contact_person =
          option?.item?.contact_person?._id || option?.item?.contact_person;
        const parent =
          contact_person ||
          option?.item?.parents[0]?._id ||
          option?.item?.parents[0];
        setStudentParent(parent);
      },
      itemCallback: item => {
        return {
          key: item._id,
          label: `${item?.description?.lastname} ${
            item?.description?.firstname
          }`,
          value: item._id,
          item: item,
        };
      },
    };

    return (
      <>
        <SelectFieldAxios
          isSearch={true}
          sortBy="label"
          className="axios-select"
          placeholder={t("inputs.selectStudent")}
          tagRender={newElementSelect.tagRender}
          itemCallback={newElementSelect.itemCallback}
          item={newElementSelect}
          onSelect={newElementSelect.onSelect}
          endponit={newElementSelect.endponit}
          endponitSend={newElementSelect.endponitSend}
        />
        {studentParentRef?.current && (
          <Form.Item
            name="parent"
            hidden={true}
            initialValue={studentParentRef.current}
            rules={[
              {
                required: false,
                message: "",
              },
            ]}
          >
            <Input />
          </Form.Item>
        )}
      </>
    );
  }, [studentParentRef, selectedStudent, localUser, i18n?.language]);

  const uploadMediaFiles = useCallback(data => {
    setMediaFiles(data);
    setUploading(false);
  }, []);
  const fileOnRemove = async file => {
    setRemoveLoading(true);
    try {
      if (typeof file === "string") {
        await api.media.delete(file);
      } else if (mediaFiles?.media?.length > 0) {
        mediaFiles?.map(async fileUrl => {
          if (typeof fileUrl === "string") {
            await api.media.delete(fileUrl);
          }
        });
      }
      setRemoveLoading(false);
    } catch (err) {
      setRemoveLoading(false);
      console.error(err);
    }

    AbsFormSickness.setFieldsValue({
      media: [],
    });
    setMediaFiles([]);
  };

  const getStatuses = useCallback(absenceType => getStatusesList(absenceType), [i18n?.language]);

  return (
    <div className={childClassName}>
      <Form
        form={AbsFormSickness}
        className="form-absence__form"
        name="form_absence_sickness"
        initialValues={initFormData}
        onFinish={onFinish}
        onValuesChange={values => {
          setIsDisabled(false);
          values?.status && setAbsenceStatus(values?.status);
        }}
      >
        {localUser?.role == "teacher" && selectStudents}
        {localUser?.role == "teacher" && (
          <div className="form-row">
            <div className="form-row__fileds" style={{ marginBottom: "30px" }}>
              <Form.Item
                name="unjustified"
                key="unjustified"
                className="forms-field forms-field--checkbox"
                valuePropName="checked"
              >
                <Checkbox
                  disabled={formData?.type == "unjustified"}
                  checked={
                    formData?.type == "unjustified" || !!formData?.unjustified
                  }
                >
                  <span className="checkbox-label">{t("absences.unjustifiedAbsence")}</span>
                </Checkbox>
              </Form.Item>
            </div>
          </div>
        )}

        {localUser?.role == "teacher" && (
          <>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.from")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_from"
                  value={currentDay}
                  onChange={val => {
                    setCurrentDay(val);
                  }}
                />
                <CustomTime
                  name="time_from"
                  label="inputs.startTime"
                  value={currentTime}
                  onChange={val => {
                    setCurrentTime(val);
                  }}
                />
              </div>
            </div>
            <div className="form-row">
              <h5 className="form-row__label">{t("common.to")}</h5>
              <div className="form-row__fileds">
                <CustomDate
                  name="date_to"
                  required={false}
                  loading={!!uploading}
                  disabled={absenceStatus == "sick"}
                />
                <CustomTime
                  name="time_to"
                  label="inputs.endTime"
                  required={false}
                  loading={!!uploading}
                  disabled={absenceStatus == "sick"}
                />
              </div>
            </div>
          </>
        )}

        <div className="sickness-media-upload">
          <h3 className="media-title">{t("absences.absenceProof")}</h3>
          {mediaFiles?.length == 0 && (
            <div
              className={`proof-option-upload ${uploading ? "loading" : ""}`}
            >
              <UploadMedia
                name="media"
                media={mediaFiles}
                setProgress={setUploading}
                uploadChange={uploadMediaFiles}
              />
            </div>
          )}
          {mediaFiles?.length > 0 && (
            <PreviewMedia
              file={mediaFiles?.[0]}
              showTitle={false}
              remove={fileOnRemove}
            />
          )}
        </div>

        <div className="form-row remark-textarea-row">
          <h5 className="form-row__label">{t("common.note")}</h5>
          <div className="form-row__fileds">
            <Form.Item
              name="remark"
              className="remark-textarea"
              rules={[
                {
                  required: false,
                },
              ]}
            >
              <TextArea disabled={false} row="5" placeholder={t("common.note")} />
            </Form.Item>
          </div>
        </div>

        {localUser?.role == "teacher" && (
          <CustomSelect
            className={`accent-label select-status ${absenceStatus}`}
            label={t("common.status")}
            value={absenceStatus}
            placeholder={t("inputs.select")}
            name={`status`}
            options={getStatuses(formData?.type || "sickness")}
          />
        )}

        <div className="absence-actions-wrapper">
          <Button
            className="btn-previous"
            onClick={() => !!prevStep && prevStep()}
          >
            {t("common.previous")}
          </Button>
          <Button
            className="confirm-btn"
            form="form_absence_sickness"
            htmlType="submit"
            disabled={isDisabled}
          >
            {t("common.confirm")}
          </Button>
        </div>
      </Form>
    </div>
  );
}

SicknessForm.defaultProps = {};
SicknessForm.propTypes = {
  result: PropTypes.func.isRequired,
  nextStep: PropTypes.func.isRequired,
  prevStep: PropTypes.func.isRequired,
  goToStep: PropTypes.func.isRequired,
};

export default memo(SicknessForm);
